import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import {
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useDocumentTitle } from 'usehooks-ts';
import { useAuthHttp } from '@hooks';
import {
  MASTER_DATA_IMPORT_APPROVE,
  MASTER_DATA_IMPORT_DISCARD,
  MASTER_DATA_IMPORT_GET,
  MASTER_DATA_IMPORT_LIST,
} from '@constants';
import { PageHeader } from '@tailwindComponents/PageHeader';
import { Button } from '@tailwindComponents/Button';
import { Badge } from '@tailwindComponents/Badge';
import { Checkbox } from '@tailwindComponents/Checkbox';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { analysedImportIdsAtom, wingmanOutboxAtom } from '../../store/wingman';
import {
  errorResponseStatusCodeAtom,
  responseErrorMessagesAtom,
} from '../../store/jotai';
import {
  ApiMasterDataImport,
  EditableFieldConfig,
  ImportEntityType,
  MappedApproveErrors,
  ReviewRow,
  buildApproveRows,
  buildRowErrorLookup,
  changedFields,
  displayValue,
  entityImportConfig,
  mapApproveErrors,
  mergeSuggestionsWithOriginal,
  rowBlockers,
} from './importApi';

const emptyApproveErrors: MappedApproveErrors = {
  rowErrors: [],
  generalErrors: [],
};

const POLL_INTERVAL_MS = 5000;

const capitalise = (value: string): string =>
  value.length === 0 ? value : value[0].toUpperCase() + value.slice(1);

const issueBadgeIntent = (severity: string | null) => {
  if (severity === 'error') return 'danger' as const;
  if (severity === 'warning') return 'warning' as const;
  return 'info' as const;
};

const fieldBorderClass = (hasError: boolean, isChanged: boolean): string => {
  if (hasError) return 'border-red-400 bg-red-50';
  if (isChanged) return 'border-blue-300 bg-blue-50';
  return 'border-gray-200';
};

export function MasterDataImportReview() {
  useDocumentTitle('FLOX - Import review');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const authHttp = useAuthHttp();

  const [importDoc, setImportDoc] = useState<ApiMasterDataImport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reviewRows, setReviewRows] = useState<ReviewRow[]>([]);
  const [reviewMode, setReviewMode] = useState<'ai' | 'manual' | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [approveErrors, setApproveErrors] =
    useState<MappedApproveErrors>(emptyApproveErrors);

  const [analysedImportIds, setAnalysedImportIds] = useAtom(
    analysedImportIdsAtom
  );
  const setWingmanOutbox = useSetAtom(wingmanOutboxAtom);
  const setErrorResponseStatusCode = useSetAtom(errorResponseStatusCodeAtom);
  const setResponseErrorMessages = useSetAtom(responseErrorMessagesAtom);
  const hasQueuedAnalyseRequest = useRef(false);

  const entityType: ImportEntityType = importDoc?.entityType ?? 'Customer';
  const config = entityImportConfig[entityType];
  const { masterRoute } = config;

  const fetchImport = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await authHttp.post(MASTER_DATA_IMPORT_GET, { id });
      const fetched = data.import as ApiMasterDataImport;
      setImportDoc(fetched);
      setLoadError(null);
    } catch (error: any) {
      setLoadError(
        error?.response?.data?.message ?? 'This import could not be loaded.'
      );
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchImport();
  }, [fetchImport]);

  useEffect(() => {
    if (!importDoc || !id) return;
    if (importDoc.status !== 'PendingAnalysis') return;
    if (hasQueuedAnalyseRequest.current) return;
    hasQueuedAnalyseRequest.current = true;
    setWingmanOutbox((outbox) =>
      outbox.some((message) => message.importId === id)
        ? outbox
        : [
            ...outbox,
            {
              type: 'flox-analyse-import',
              importId: id,
              entityType: importDoc.entityType,
            },
          ]
    );
  }, [importDoc, id, setWingmanOutbox]);

  useEffect(() => {
    if (!importDoc || !id) return undefined;
    if (
      importDoc.status !== 'PendingAnalysis' &&
      importDoc.status !== 'Approving'
    ) {
      return undefined;
    }
    const poll = setInterval(async () => {
      try {
        const { data } = await authHttp.post(MASTER_DATA_IMPORT_LIST, {
          id,
          pagination: { itemsPerPage: 1 },
        });
        const slim = data.items?.[0];
        if (slim && slim.status !== importDoc.status) {
          fetchImport();
        }
      } catch {
        /* polling is best-effort; the next tick retries */
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importDoc?.status, id]);

  useEffect(() => {
    if (!id || !analysedImportIds.includes(id)) return;
    setAnalysedImportIds((ids) =>
      ids.filter((analysedId) => analysedId !== id)
    );
    fetchImport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysedImportIds, id]);

  useEffect(() => {
    if (!importDoc) return;
    if (importDoc.status === 'Analysed' && reviewMode === null) {
      setReviewMode('ai');
      setReviewRows(mergeSuggestionsWithOriginal(importDoc, true));
    }
  }, [importDoc, reviewMode]);

  const startManualReview = () => {
    if (!importDoc) return;
    setReviewMode('manual');
    setReviewRows(mergeSuggestionsWithOriginal(importDoc, false));
  };

  const { editableFields } = config;

  const rowErrorLookup = useMemo(
    () => buildRowErrorLookup(reviewRows, entityType, approveErrors.rowErrors),
    [reviewRows, entityType, approveErrors]
  );

  const attentionCount = useMemo(
    () =>
      reviewRows.filter((row) => rowBlockers(row, entityType).length > 0)
        .length,
    [reviewRows, entityType]
  );

  const updateRowField = (
    key: string,
    field: EditableFieldConfig,
    value: string
  ) => {
    setApproveErrors(emptyApproveErrors);
    setReviewRows((rows) =>
      rows.map((row) =>
        row.key === key
          ? { ...row, data: field.setValue(row.data, value) }
          : row
      )
    );
  };

  const toggleRowExcluded = (key: string) => {
    setApproveErrors(emptyApproveErrors);
    setReviewRows((rows) =>
      rows.map((row) =>
        row.key === key ? { ...row, excluded: !row.excluded } : row
      )
    );
  };

  const toggleRowExpanded = (key: string) => {
    setExpandedRows((expanded) => {
      const next = new Set(expanded);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const includedRowCount = reviewRows.filter((row) => !row.excluded).length;

  const resolveApproveLabel = () => {
    if (isSubmitting) return 'Approving...';
    if (attentionCount > 0) {
      return `Approve (${attentionCount} row(s) need attention)`;
    }
    return `Approve ${includedRowCount} row(s)`;
  };

  const clearGlobalErrorDialog = () => {
    setErrorResponseStatusCode(null);
    setResponseErrorMessages([]);
  };

  const handleApprove = async () => {
    if (!importDoc || !id) return;

    if (attentionCount > 0) {
      setApproveErrors({
        rowErrors: [],
        generalErrors: [
          `${attentionCount} row(s) need attention before this import can be approved. Fix the highlighted fields or exclude those rows.`,
        ],
      });
      return;
    }

    setIsSubmitting(true);
    setApproveErrors(emptyApproveErrors);
    const approveRows = buildApproveRows(reviewRows, entityType);
    try {
      const body = { id, [config.approveBodyKey]: approveRows };
      await authHttp.post(MASTER_DATA_IMPORT_APPROVE, body);
      navigate(masterRoute);
    } catch (error: any) {
      const mapped = mapApproveErrors(
        error?.response?.data?.errors,
        approveRows,
        entityType
      );
      if (mapped.rowErrors.length === 0 && mapped.generalErrors.length === 0) {
        mapped.generalErrors.push(
          error?.response?.data?.message ??
            'The import could not be approved. Please try again.'
        );
      }
      setApproveErrors(mapped);
      clearGlobalErrorDialog();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscard = async () => {
    if (!id) return;
    setIsSubmitting(true);
    try {
      await authHttp.post(MASTER_DATA_IMPORT_DISCARD, { id });
      navigate(masterRoute);
    } catch (error: any) {
      setApproveErrors({
        rowErrors: [],
        generalErrors: [
          error?.response?.data?.message ??
            'The import could not be discarded.',
        ],
      });
      clearGlobalErrorDialog();
    } finally {
      setIsSubmitting(false);
    }
  };

  const pages = [
    { name: 'Home', href: '/', current: false },
    {
      name: capitalise(config.nouns.plural),
      href: masterRoute,
      current: false,
    },
    { name: 'Import review', href: '#', current: true },
  ];

  if (isLoading) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <FloxSpinner />
      </div>
    );
  }

  if (loadError || !importDoc) {
    return (
      <main className='flex flex-col items-center gap-4 px-8 py-16 font-inter'>
        <ExclamationTriangleIcon className='h-10 w-10 text-amber-500' />
        <p className='text-gray-700'>{loadError ?? 'Import not found.'}</p>
        <Button intent='secondary' label='Back' onClick={() => navigate(-1)} />
      </main>
    );
  }

  const isAwaitingAnalysis =
    importDoc.status === 'PendingAnalysis' && reviewMode !== 'manual';
  const isReviewing =
    reviewMode !== null &&
    (importDoc.status === 'Analysed' ||
      importDoc.status === 'PendingAnalysis' ||
      importDoc.status === 'AnalysisFailed');

  return (
    <>
      <PageHeader
        header='Import review'
        subHeader={`${importDoc.fileName} — ${importDoc.rowCount} row(s)`}
        pages={pages}
      >
        {isReviewing && (
          <div className='flex items-center gap-2'>
            <Button
              intent='danger'
              label='Discard import'
              startIcon={<TrashIcon className='h-5 w-5' />}
              onClick={handleDiscard}
              disabled={isSubmitting}
            />
            <Button
              intent='primary'
              label={resolveApproveLabel()}
              startIcon={<CheckCircleIcon className='h-5 w-5' />}
              onClick={handleApprove}
              disabled={isSubmitting || includedRowCount === 0}
            />
          </div>
        )}
      </PageHeader>
      <main className='flex flex-col gap-4 px-8 py-6 font-inter'>
        {isAwaitingAnalysis && (
          <section className='flex flex-col items-center gap-4 rounded-lg border border-gray-200 bg-white p-10 shadow-sm'>
            <SparklesIcon className='h-10 w-10 text-primary' />
            <h2 className='text-lg font-semibold text-gray-900'>
              Wingman is analysing your file…
            </h2>
            <p className='max-w-lg text-center text-sm text-gray-500'>
              Wingman is reading {importDoc.fileName} and preparing suggested{' '}
              {config.nouns.singular} records for you to review. This page
              refreshes automatically.
            </p>
            <FloxSpinner />
            <div className='flex items-center gap-2 pt-2'>
              {importDoc.templateRecognised && (
                <Button
                  intent='secondary'
                  label='Review without AI'
                  onClick={startManualReview}
                  disabled={isSubmitting}
                />
              )}
              <Button
                intent='danger'
                label='Discard import'
                onClick={handleDiscard}
                disabled={isSubmitting}
              />
            </div>
            {!importDoc.templateRecognised && importDoc.raw && (
              <div className='w-full overflow-x-auto pt-4'>
                <p className='pb-2 text-xs text-gray-500'>
                  Preview of the uploaded file (first 10 rows):
                </p>
                <table className='min-w-full text-left text-xs text-gray-600'>
                  <tbody>
                    {importDoc.raw.rows.slice(0, 10).map((rawRow) => (
                      <tr
                        key={rawRow.sourceRow}
                        className='border-b border-gray-100'
                      >
                        <td className='py-1 pr-3 text-gray-400'>
                          {rawRow.sourceRow}
                        </td>
                        {rawRow.cells.slice(0, 8).map((cell, cellIndex) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <td key={cellIndex} className='py-1 pr-3'>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {importDoc.status === 'AnalysisFailed' && reviewMode !== 'manual' && (
          <section className='flex flex-col items-center gap-4 rounded-lg border border-amber-200 bg-amber-50 p-10'>
            <ExclamationTriangleIcon className='h-10 w-10 text-amber-500' />
            <h2 className='text-lg font-semibold text-gray-900'>
              Wingman could not analyse this file
            </h2>
            {importDoc.analysisError && (
              <p className='max-w-lg text-center text-sm text-gray-600'>
                {importDoc.analysisError}
              </p>
            )}
            <div className='flex items-center gap-2'>
              {importDoc.templateRecognised && (
                <Button
                  intent='secondary'
                  label='Review without AI'
                  onClick={startManualReview}
                  disabled={isSubmitting}
                />
              )}
              <Button
                intent='danger'
                label='Discard import'
                onClick={handleDiscard}
                disabled={isSubmitting}
              />
            </div>
          </section>
        )}

        {(importDoc.status === 'Approved' ||
          importDoc.status === 'Approving') && (
          <section className='flex flex-col items-center gap-4 rounded-lg border border-emerald-200 bg-emerald-50 p-10'>
            <CheckCircleIcon className='h-10 w-10 text-emerald-600' />
            <h2 className='text-lg font-semibold text-gray-900'>
              {importDoc.status === 'Approved'
                ? `Import approved — ${
                    importDoc.numberOfRecordsCreated ?? 0
                  } record(s) created`
                : 'This import is being approved…'}
            </h2>
            {importDoc.status === 'Approved' && (
              <div className='flex flex-col items-center gap-1 text-sm text-gray-600'>
                {typeof importDoc.numberOfExistingSkusSkipped === 'number' &&
                  importDoc.numberOfExistingSkusSkipped > 0 && (
                    <p>
                      {importDoc.numberOfExistingSkusSkipped} skipped — already
                      in your catalogue
                    </p>
                  )}
                {typeof importDoc.numberOfExistingSkusUpdatedWithImages ===
                  'number' &&
                  importDoc.numberOfExistingSkusUpdatedWithImages > 0 && (
                    <p>
                      {importDoc.numberOfExistingSkusUpdatedWithImages} existing
                      SKU(s) updated with images
                    </p>
                  )}
                {typeof importDoc.numberOfImagesImported === 'number' &&
                  importDoc.numberOfImagesImported > 0 && (
                    <p>{importDoc.numberOfImagesImported} image(s) imported</p>
                  )}
              </div>
            )}
            <Button
              intent='primary'
              label={`Go to ${config.nouns.plural}`}
              onClick={() => navigate(masterRoute)}
            />
          </section>
        )}

        {importDoc.status === 'Discarded' && (
          <section className='flex flex-col items-center gap-4 rounded-lg border border-gray-200 bg-white p-10 shadow-sm'>
            <TrashIcon className='h-10 w-10 text-gray-400' />
            <h2 className='text-lg font-semibold text-gray-900'>
              This import was discarded
            </h2>
            <Button
              intent='secondary'
              label='Back'
              onClick={() => navigate(masterRoute)}
            />
          </section>
        )}

        {isReviewing && (
          <>
            {reviewMode === 'ai' && importDoc.analysis?.summary && (
              <section className='flex items-start gap-3 rounded-md bg-blue-50 px-4 py-3'>
                <SparklesIcon className='mt-0.5 h-5 w-5 shrink-0 text-primary' />
                <div>
                  <p className='text-sm font-semibold text-gray-900'>
                    Wingman&apos;s analysis
                  </p>
                  <p className='text-sm text-gray-700'>
                    {importDoc.analysis.summary}
                  </p>
                </div>
              </section>
            )}
            {reviewMode === 'manual' && (
              <section className='rounded-md bg-gray-100 px-4 py-3 text-sm text-gray-700'>
                Reviewing the file without AI suggestions — rows come from the
                uploaded template.
              </section>
            )}
            {approveErrors.generalErrors.length > 0 && (
              <section className='flex flex-col gap-1 rounded-md border border-red-200 bg-red-50 px-4 py-3'>
                <p className='text-sm font-semibold text-red-700'>
                  The import could not be approved:
                </p>
                {approveErrors.generalErrors.map((message) => (
                  <p key={message} className='text-sm text-red-700'>
                    {message}
                  </p>
                ))}
              </section>
            )}
            <section className='overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm'>
              <table className='min-w-[960px] w-full text-left text-sm'>
                <thead>
                  <tr className='border-b border-gray-200 text-xs uppercase text-gray-500'>
                    <th className='w-10 px-3 py-3' aria-label='Expand' />
                    <th className='w-16 px-3 py-3'>Row</th>
                    {editableFields.map((field) => (
                      <th key={field.key} className='px-3 py-3'>
                        {field.label}
                      </th>
                    ))}
                    {importDoc.entityType === 'Supplier' && (
                      <th className='px-3 py-3'>Locations</th>
                    )}
                    <th className='px-3 py-3'>Issues</th>
                    <th className='w-24 px-3 py-3'>Include</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewRows.map((row) => {
                    const isExpanded = expandedRows.has(row.key);
                    const rowChangedFields = changedFields(
                      row.original,
                      row.suggested
                    );
                    const rowErrorState = rowErrorLookup.get(row.key);
                    const locations = row.data.locations as
                      | Record<string, unknown>[]
                      | null
                      | undefined;
                    const totalColumns =
                      editableFields.length +
                      (importDoc.entityType === 'Supplier' ? 5 : 4);
                    return (
                      <Fragment key={row.key}>
                        <tr
                          className={`border-b border-gray-100 ${
                            row.excluded ? 'bg-gray-50 opacity-60' : ''
                          }`}
                        >
                          <td className='w-10 px-3 py-2'>
                            <button
                              type='button'
                              aria-label={`Expand row ${row.sourceRow}`}
                              onClick={() => toggleRowExpanded(row.key)}
                              className='rounded p-1 hover:bg-gray-100'
                            >
                              <ChevronDownIcon
                                className={`h-4 w-4 text-gray-500 transition-transform ${
                                  isExpanded ? 'rotate-180' : ''
                                }`}
                              />
                            </button>
                          </td>
                          <td className='w-16 px-3 py-2 text-gray-500'>
                            {row.sourceRow}
                          </td>
                          {editableFields.map((field) => {
                            const fieldErrors =
                              rowErrorState?.fieldMessages.get(field.key) ?? [];
                            const hasFieldError = fieldErrors.length > 0;
                            return (
                              <td
                                key={field.key}
                                className='px-3 py-2 align-top'
                              >
                                <input
                                  type='text'
                                  value={String(field.getValue(row.data) ?? '')}
                                  onChange={(event) =>
                                    updateRowField(
                                      row.key,
                                      field,
                                      event.target.value
                                    )
                                  }
                                  disabled={row.excluded}
                                  className={`w-full rounded-md border px-2 py-1 text-sm focus:border-primary focus:outline-none ${fieldBorderClass(
                                    hasFieldError,
                                    rowChangedFields.includes(field.key)
                                  )}`}
                                />
                                {hasFieldError && (
                                  <div className='mt-1 flex flex-col gap-0.5'>
                                    {fieldErrors.map((message) => (
                                      <p
                                        key={message}
                                        className='text-xs text-red-700'
                                      >
                                        {message}
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          {importDoc.entityType === 'Supplier' && (
                            <td className='px-3 py-2 text-gray-500'>
                              {locations?.length ?? 0} location(s)
                            </td>
                          )}
                          <td className='px-3 py-2 align-top'>
                            <div className='flex flex-wrap gap-1'>
                              {(rowErrorState?.rowMessages ?? []).map(
                                (message) => (
                                  <Badge
                                    key={`blocker-${message}`}
                                    intent='danger'
                                    label={message}
                                  />
                                )
                              )}
                              {row.parseErrors.map((parseError) => (
                                <Badge
                                  key={parseError}
                                  intent='danger'
                                  label={parseError}
                                />
                              ))}
                              {row.issues.map((issue) => (
                                <Badge
                                  key={`${issue.field}-${issue.message}`}
                                  intent={issueBadgeIntent(issue.severity)}
                                  label={issue.message ?? 'Issue'}
                                />
                              ))}
                            </div>
                          </td>
                          <td className='w-24 px-3 py-2'>
                            <Checkbox
                              label=''
                              aria-label={`Include row ${row.sourceRow}`}
                              checked={!row.excluded}
                              onChange={() => toggleRowExcluded(row.key)}
                            />
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className='border-b border-gray-100 bg-gray-50'>
                            <td colSpan={totalColumns} className='px-6 py-3'>
                              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                                <div>
                                  <p className='pb-1 text-xs font-semibold uppercase text-gray-500'>
                                    Original (from file)
                                  </p>
                                  {row.original ? (
                                    <dl className='text-xs text-gray-600'>
                                      {Object.entries(row.original).map(
                                        ([key, value]) => (
                                          <div key={key} className='flex gap-2'>
                                            <dt className='w-40 shrink-0 text-gray-400'>
                                              {key}
                                            </dt>
                                            <dd>{displayValue(value)}</dd>
                                          </div>
                                        )
                                      )}
                                    </dl>
                                  ) : (
                                    <p className='text-xs text-gray-400'>
                                      No template parse for this row.
                                    </p>
                                  )}
                                </div>
                                <div>
                                  <p className='pb-1 text-xs font-semibold uppercase text-gray-500'>
                                    Wingman&apos;s suggestion
                                  </p>
                                  {row.suggested ? (
                                    <dl className='text-xs text-gray-600'>
                                      {Object.entries(row.suggested).map(
                                        ([key, value]) => (
                                          <div key={key} className='flex gap-2'>
                                            <dt className='w-40 shrink-0 text-gray-400'>
                                              {key}
                                            </dt>
                                            <dd
                                              className={
                                                rowChangedFields.includes(key)
                                                  ? 'font-semibold text-primary'
                                                  : ''
                                              }
                                            >
                                              {displayValue(value)}
                                            </dd>
                                          </div>
                                        )
                                      )}
                                    </dl>
                                  ) : (
                                    <p className='text-xs text-gray-400'>
                                      No AI suggestion for this row.
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </section>
          </>
        )}
      </main>
    </>
  );
}
